"use client"

import { useCallback, useMemo } from "react"
import { useAppKit } from "@reown/appkit/react"
import {
  useAccount,
  useConnect,
  useDisconnect,
  useSignMessage,
  useSendTransaction,
} from "wagmi"

import {
  buildWalletDeeplink,
  openMobileWalletDeeplink,
  walletChains,
  type WalletChainId,
} from "../../config"
import { useWalletChain } from "../../context/wallet-context"
import { useTronWallet } from "../../hooks/use-tron-wallet"
import {
  createEvmAdapter,
  createTronAdapter,
} from "../adapters"
import {
  isMobileBrowser,
  isWalletInstalled,
  resolveConnectChain,
  shouldRedirectToWalletBrowser,
} from "../detection"
import { hasTronInjection } from "../tron-provider"
import {
  isMetaMaskTronAvailable,
  setActiveTronBackend,
} from "../metamask-tron"
import type { FeaturedWallet, WalletConnectResult } from "../types"
import {
  featuredWallets,
} from "../wallets"

function redirectToWalletBrowser(
  wallet: FeaturedWallet,
  chain: "evm" | "tron",
): never {
  const currentUrl = window.location.href

  if (wallet.id === "gemini") {
    window.open(wallet.installUrl, "_blank", "noopener,noreferrer")
    throw new Error("Gemini has no dApp browser deeplink")
  }

  const deeplink = buildWalletDeeplink(wallet.id, currentUrl, chain)
  if (deeplink === currentUrl) {
    window.open(wallet.installUrl, "_blank", "noopener,noreferrer")
  } else {
    openMobileWalletDeeplink(deeplink)
  }
  throw new Error("Redirecting to wallet app")
}

function isInvalidEvmAddressError(cause: unknown): boolean {
  const message = cause instanceof Error ? cause.message : String(cause)
  return (
    /Address "null" is invalid/i.test(message) ||
    /Address "undefined" is invalid/i.test(message) ||
    /must be a hex value of 20 bytes/i.test(message) ||
    /EVM wallet did not return an address/i.test(message)
  )
}

export function useFeaturedWalletConnect() {
  const { activeChain, setActiveChain, closeWalletModal } = useWalletChain()
  const { connectors, connectAsync } = useConnect()
  const { disconnect: disconnectEvm } = useDisconnect()
  const { address: evmAddress } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const { sendTransactionAsync } = useSendTransaction()
  const { open: openReown } = useAppKit()
  const { syncFromProvider } = useTronWallet()
  const tronAdapter = useMemo(() => createTronAdapter(), [])

  const connectTron = useCallback(
    async (wallet: FeaturedWallet): Promise<WalletConnectResult> => {
      setActiveChain("tron")
      if (wallet.tronProvider === "metamask") {
        setActiveTronBackend("metamask")
      } else {
        setActiveTronBackend("injected")
      }

      const tronInstalled = isWalletInstalled(wallet, connectors, {
        activeChain: "tron",
      })
      const hasTron = hasTronInjection()
      const metaMaskTronReady =
        wallet.tronProvider === "metamask" &&
        (tronInstalled || (await isMetaMaskTronAvailable()))

      if (metaMaskTronReady || tronInstalled || hasTron) {
        const result = await tronAdapter.connect()
        syncFromProvider()
        closeWalletModal()
        return result
      }

      if (isMobileBrowser()) {
        redirectToWalletBrowser(wallet, "tron")
      }

      window.open(wallet.installUrl, "_blank", "noopener,noreferrer")
      throw new Error(`${wallet.name} Tron provider not found`)
    },
    [
      closeWalletModal,
      connectors,
      setActiveChain,
      syncFromProvider,
      tronAdapter,
    ],
  )

  const connectFeatured = useCallback(
    async (
      wallet: FeaturedWallet,
      selectedChain: WalletChainId = activeChain,
    ): Promise<WalletConnectResult> => {
      setActiveChain(selectedChain)
      const connectChain = resolveConnectChain(wallet, selectedChain)

      if (shouldRedirectToWalletBrowser()) {
        redirectToWalletBrowser(wallet, connectChain)
      }

      if (connectChain === "tron") {
        return connectTron(wallet)
      }

      const mobile = isMobileBrowser()
      const installed = isWalletInstalled(wallet, connectors, {
        activeChain: selectedChain,
      })
      const targetChainId = walletChains[selectedChain].chainId

      if (installed) {
        try {
          const adapter = createEvmAdapter(wallet, {
            connectors,
            connectAsync,
            disconnect: disconnectEvm,
            getAccount: () => evmAddress,
            signMessage: signMessageAsync,
            sendTransaction: sendTransactionAsync,
            chainId: targetChainId,
          })
          const result = await adapter.connect()
          closeWalletModal()
          return result
        } catch (cause) {
          // Dual-chain wallets inside a TRON DApp browser often expose a broken
          // ethereum provider (selectedAddress = null). Retry via Tron.
          if (wallet.tronProvider && isInvalidEvmAddressError(cause)) {
            return connectTron(wallet)
          }
          throw cause
        }
      }

      if (mobile) {
        redirectToWalletBrowser(wallet, connectChain)
      }

      window.open(wallet.installUrl, "_blank", "noopener,noreferrer")
      throw new Error(`${wallet.name} extension not found`)
    },
    [
      activeChain,
      closeWalletModal,
      connectAsync,
      connectTron,
      connectors,
      disconnectEvm,
      evmAddress,
      sendTransactionAsync,
      setActiveChain,
      signMessageAsync,
    ],
  )

  const connectFeaturedById = useCallback(
    async (walletId: string, selectedChain?: WalletChainId) => {
      const wallet = featuredWallets.find((item) => item.id === walletId)
      if (!wallet) {
        throw new Error(`Unknown wallet: ${walletId}`)
      }
      return connectFeatured(wallet, selectedChain)
    },
    [connectFeatured],
  )

  const openOtherWallets = useCallback(async () => {
    setActiveChain("ethereum")
    closeWalletModal()
    // Let the custom dialog close before AppKit opens (avoids z-index / focus traps).
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve())
      })
    })
    openReown({ view: "Connect" })
  }, [closeWalletModal, openReown, setActiveChain])

  const detectedById = useMemo(
    () =>
      Object.fromEntries(
        featuredWallets.map((wallet) => [
          wallet.id,
          isWalletInstalled(wallet, connectors, { activeChain }),
        ]),
      ) as Record<string, boolean>,
    [activeChain, connectors],
  )

  return {
    connectFeatured,
    connectFeaturedById,
    openOtherWallets,
    walletDetectedMap: detectedById,
  }
}
